import Phaser from 'phaser';
import halloween from '../../../server/api/src/game-config/halloween.config.json';
import { assetBundleManager } from '@/game/assets/AssetBundleManager';
import { Player } from '@/game/entities/Player';
import { Pet } from '@/game/entities/Pet';
import { ArenaTarget } from '@/game/entities/PetTarget';
import { HalloweenCandyCollector } from '@/game/systems/HalloweenCandyCollector';
import { InputSystem } from '@/game/systems/InputSystem';
import { PETS_BY_ID } from '@/config/pets.config';
import { eventBus } from '@/shared/EventBus';
import { saveManager } from '@/save';
import { useGameStore } from '@/ui/store/gameStore';
import { halloweenBossApi, ApiError, type HalloweenBossView, type BossCandy, type BossGust } from '@/net/api';
import { runEconomyAction, ECONOMY_RESYNC_CODE, ECONOMY_RESYNC_MESSAGE } from '@/net/economyActions';
import { startTeleport } from '@/game/fx/teleport';
import { HalloweenBossPresentation } from '@/game/fx/HalloweenBossPresentation';
import { HalloweenRottenCandyFx } from '@/game/fx/HalloweenRottenCandyFx';

/** A personal, persistent pinata encounter: collecting fills the meter, never combat HP. */
export class HalloweenBossScene extends Phaser.Scene {
  private controls!: InputSystem;
  private player!: Player;
  private pets: Pet[] = [];
  private petTargets: ArenaTarget[] = [];
  private petAttackSlots: { x: number; y: number }[] = [];
  private petFootprints: number[] = [];
  private boss!: Phaser.GameObjects.Sprite;
  private presentation!: HalloweenBossPresentation;
  private bossBaseY = 300;
  private won = false;
  private drops = new Map<string, { candy: BossCandy; sprite: Phaser.GameObjects.Image; glow: Phaser.GameObjects.Arc; rottenFx?: HalloweenRottenCandyFx;
    collecting?: boolean; pickupSprite?: Phaser.GameObjects.Image; blownByGust?: number }>();
  private gustIndex = 0;
  private gustCamera = false;
  private retreatFollow = false;
  private gustCameraTween?: Phaser.Tweens.Tween;
  private gustMotion?: { startedAt: number; player: { from: {x:number;y:number}; to: {x:number;y:number} };
    pets: { from: {x:number;y:number}; to: {x:number;y:number} }[] };
  private view: HalloweenBossView | null = null;
  private busy = false;
  private dead = true;
  private run = 0;
  private nextPulse = 0;
  private phase = 0;
  private introDecisionPending = true;
  private introQueued = false;
  private introPlaying = false;
  private devIntroPlayed = false;
  private lastPulseStartedAt = -Infinity;
  private readonly collector = new HalloweenCandyCollector(halloween.boss.pickupRadius, halloween.boss.rotten.pickupRadius);
  private tapDestination: Phaser.Math.Vector2 | null = null;
  constructor() { super({ key: 'HalloweenBossScene' }); }

  create(): void {
    this.collector.clear(); this.lastPulseStartedAt = -Infinity; this.won = false;
    this.run++; this.dead = false; this.busy = false; this.view = null;
    this.nextPulse = 0; this.phase = 0; this.pets = []; this.drops = new Map(); this.tapDestination = null;
    this.gustIndex = 0; this.gustMotion = undefined;
    this.gustCamera = false; this.retreatFollow = false;
    this.introDecisionPending = true; this.introQueued = false; this.introPlaying = false;
    useGameStore.getState().setBossIntroActive(false);
    this.petTargets = []; this.petAttackSlots = []; this.petFootprints = [];
    assetBundleManager.activateScene(this.textures, this.anims, 'scene:halloween-boss', saveManager.getCurrent());
    useGameStore.getState().setActiveScene('halloweenBoss');
    this.cameras.main.setBackgroundColor('#c4cda6');
    this.physics.world.setBounds(35, 380, 830, 900);
    this.add.tileSprite(-1500, -1500, 3900, 4000, 'bg_capibara_pumpkin_patch_calm_tile').setOrigin(0).setDepth(-100);
    this.add.ellipse(450, 650, 820, 600, 0xf5deb6, 0.9).setStrokeStyle(14, 0xc2a17d, 0.4).setDepth(-90);
    this.add.ellipse(450, 650, 730, 500, 0xfff0cd, 0.6).setDepth(-89);
    for (const x of [70, 830]) {
      this.add.image(x, 110, 'deco_halloween_autumn_grove').setDisplaySize(240, 240).setAlpha(0.65).setDepth(-80);
    }
    this.add.image(450, 105, 'deco_halloween_festival_lights').setDisplaySize(400, 270).setAlpha(0.8).setDepth(-70);
    this.presentation = new HalloweenBossPresentation(this);
    this.boss = this.presentation.sprite;
    this.resetBossPose();
    this.controls = new InputSystem(); this.controls.attach(this);
    const save = saveManager.getCurrent();
    this.player = new Player(this, 450, 760, this.controls, save.profile.avatar.skinId);
    this.player.speed = 310;
    this.player.sprite.setScale(this.player.sprite.scaleX * 1.5, this.player.sprite.scaleY * 1.5);
    this.player.animator?.setBaseScale(this.player.sprite.scaleX);
    const equipped = save.equipped.map(uid => save.inventory.find(p => p.uid === uid)).filter(p => !!p);
    equipped.forEach((instance, index) => {
      const def = PETS_BY_ID[instance.defId];
      if (def) {
        const pet = new Pet(this, def, instance, index, equipped.length, this.player);
        this.pets.push(pet);
        // Reserve room for squash, rotation and the occasional sparkle scale pulse.
        this.petFootprints.push(Math.max(pet.sprite.displayWidth, pet.sprite.displayHeight) * 1.6 + 20);
      }
    });
    this.petTargets = this.pets.map((_, index) => new ArenaTarget(() => {
      const slot = this.petAttackSlots[index]!;
      return { x: slot.x, y: slot.y - 28 };
    }, 1, () => this.hop(), false));
    const offExit = eventBus.on('halloweenBoss:exit', () => {
      startTeleport(this, { target: 'WorldScene', accent: 0xffc478 });
    });
    const offRetry = eventBus.on('halloweenBoss:retry', () => {
      this.nextPulse = 0;
      if (this.introDecisionPending) void this.prepareEncounter();
      else if (!this.view?.fight && !this.introQueued && !this.introPlaying) void this.enter();
    });
    // Mouse/tap-to-walk supplements the existing keyboard and mobile joystick.
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.gustMotion || this.introDecisionPending || this.introQueued || this.introPlaying) return;
      if (pointer.getDistance() > 18 || pointer.wasCanceled) return;
      const p = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      this.tapDestination = new Phaser.Math.Vector2(Phaser.Math.Clamp(p.x, 35, 865), Phaser.Math.Clamp(p.y, 380, 1260));
    });
    // BootScene uses FIT globally. The arena needs real viewport dimensions on rotation.
    const previousScaleMode = this.scale.scaleMode;
    this.scale.scaleMode = Phaser.Scale.RESIZE;
    this.scale.refresh();
    this.layout(); this.scale.on('resize', this.layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.dead = true; this.run++; this.controls.detach();
      this.scene.stop('HalloweenBossIntroScene');
      useGameStore.getState().setBossIntroActive(false);
      this.presentation.destroy();
      this.gustCameraTween?.remove();
      this.petTargets.forEach(target => target.setTargetable(false));
      this.pets.forEach(pet => pet.destroy()); this.pets = [];
      this.petTargets = []; this.petAttackSlots = []; this.petFootprints = [];
      this.collector.clear(); this.drops.forEach(drop => drop.rottenFx?.destroy()); this.drops.clear();
      this.scale.off('resize', this.layout, this); offExit(); offRetry();
      // Keep the current canvas dimensions after rotation, avoiding a stretched world.
      this.scale.scaleMode = previousScaleMode;
      this.scale.refresh();
    });
    void this.prepareEncounter();
  }
  private async prepareEncounter(): Promise<void> {
    if (this.busy || this.dead) return;
    const run = this.run; this.busy = true;
    let shouldEnter = false;
    try {
      const view = await halloweenBossApi.state();
      if (this.dead || run !== this.run) return;
      this.accept(view); this.introDecisionPending = false;
      const localReplay = import.meta.env.DEV && !this.devIntroPlayed && view.active &&
        new URLSearchParams(location.search).get('halloweenBossIntro') === '1';
      if ((view.status === 'ready' && !view.introSeen) || localReplay) {
        this.devIntroPlayed = true;
        this.introQueued = true;
        useGameStore.getState().setBossIntroActive(true);
      } else shouldEnter = view.status !== 'inactive';
    } catch (error) { if (!this.dead && run === this.run) this.showConnectionMessage(error); }
    finally {
      if (run === this.run) { this.busy = false; this.nextPulse = this.time.now + halloween.boss.pulseMs; }
    }
    if (shouldEnter && !this.dead && run === this.run) void this.enter();
  }
  private startIntro(): void {
    this.introQueued = false; this.introPlaying = true;
    const run = this.run;
    this.scene.launch('HalloweenBossIntroScene', { onComplete: () => {
      if (this.dead || run !== this.run) return;
      this.introPlaying = false; this.tapDestination = null;
      eventBus.emit('input:joystick', { dx: 0, dy: 0 });
      useGameStore.getState().setBossIntroActive(false);
      void this.enter();
    } });
  }
  private layout(): void {
    const { width, height } = this.scale;
    const top = Math.min(170, height * 0.28);
    const usable = Math.max(150, height - top);
    const camera = this.cameras.main;
    this.resetBossPose(this.won);
    this.arrangePetAttackSlots();
    camera.stopFollow().removeBounds().setViewport(0, 0, width, height);
    this.retreatFollow = this.player.y > 930;
    if (this.gustCamera) { this.layoutGustCamera(); return; }
    if (width > height && height < 600) {
      // Fit the collection area through movement, keeping sweets readable on a rotated phone.
      camera.setZoom(0.65).setBounds(-250, 0, 1400, 1500);
      camera.startFollow(this.player.sprite, false, 0.16, 0.16, 0, 180);
    } else {
      camera.setZoom(Math.min(width / 900, usable / 1000)).centerOn(450, 500);
      if (this.retreatFollow) camera.startFollow(this.player.sprite, false, 0.16, 0.16, 0, 180);
    }
  }
  private layoutGustCamera(animate = false): void {
    this.gustCameraTween?.remove();
    const {width,height}=this.scale, top=Math.min(170,height*0.28);
    const camera=this.cameras.main, zoom=Math.min(width/950,(height-top)/1250);
    camera.stopFollow().removeBounds();
    if (animate) {
      this.gustCameraTween=this.tweens.add({targets:camera,zoom,duration:240,ease:'Sine.easeOut',
        onUpdate:()=>camera.centerOn(450,650)});
    } else camera.setZoom(zoom).centerOn(450,650);
  }
  private arrangePetAttackSlots(): void {
    const count = this.pets.length;
    const totalWidth = this.petFootprints.reduce((sum, width) => sum + width, 0);
    const columns = totalWidth <= 800 ? count : Math.ceil(count / 2);
    const twoRows = columns < count;
    const rowStep = Math.max(0, ...this.petFootprints);
    this.petAttackSlots = [];
    for (let start = 0; start < count; start += columns) {
      const widths = this.petFootprints.slice(start, start + columns);
      const rowWidth = widths.reduce((sum, width) => sum + width, 0);
      let cursor = 450 - rowWidth / 2;
      widths.forEach((width, column) => {
        const curve = widths.length > 1 ? Math.sin(column / (widths.length - 1) * Math.PI) * 24 : 0;
        this.petAttackSlots.push({ x: cursor + width / 2,
          y: this.bossBaseY + (twoRows ? 130 : 160) + Math.floor(start / columns) * rowStep + curve });
        cursor += width;
      });
    }
  }
  private async enter(): Promise<void> {
    if (this.busy || this.dead || this.introDecisionPending || this.introQueued || this.introPlaying) return;
    const run = this.run; this.busy = true;
    try {
      const response = await runEconomyAction(() => halloweenBossApi.enter());
      if (this.dead || run !== this.run) return;
      this.accept(response);
    } catch (error) {
      if (this.dead || run !== this.run) return;
      if (error instanceof ApiError && ['boss_cooldown', 'event_inactive'].includes(error.code)) {
        try { const state = await halloweenBossApi.state(); if (!this.dead && run === this.run) this.accept(state); }
        catch { this.showConnectionMessage(); }
      } else this.showConnectionMessage(error);
    } finally { if (run === this.run) { this.busy = false; this.nextPulse = this.time.now + halloween.boss.pulseMs; } }
  }
  private async pulse(): Promise<void> {
    const fight = this.view?.fight;
    if (!fight || this.busy || this.dead) return;
    const run = this.run; this.busy = true;
    this.lastPulseStartedAt = this.time.now;
    const current = { x: Phaser.Math.Clamp(this.player.x, 35, 865), y: Phaser.Math.Clamp(this.player.y, 380, 1280) };
    const { ids, position: { x, y } } = this.collector.batch(fight.drops, current);
    try {
      const response = await runEconomyAction(() => halloweenBossApi.pulse(fight.id, ids, x, y));
      if (this.dead || run !== this.run) return;
      this.accept(response, response.rewardGranted, ids);
    } catch (error) {
      if (this.dead || run !== this.run) return;
      if (error instanceof ApiError && ['event_inactive', 'boss_fight_changed'].includes(error.code)) {
        try { const state = await halloweenBossApi.state(); if (!this.dead && run === this.run) this.accept(state); }
        catch { this.showConnectionMessage(); }
      } else this.showConnectionMessage(error);
    } finally { if (run === this.run) { this.busy = false; this.nextPulse = this.time.now + halloween.boss.pulseMs; } }
  }
  private lastResponseAt = 0;
  private accept(view: HalloweenBossView, reward = 0, submittedIds: readonly string[] = []): void {
    const previous = this.view;
    const predictedNow = previous ? this.estimatedServerNow() : view.serverNow;
    if (!reward && previous?.fight && view.wins > previous.wins) reward = view.reward;
    if (previous?.fight?.id !== view.fight?.id) {
      this.collector.clear();
      this.gustIndex = 0; this.gustMotion = undefined; this.player.sprite.setAngle(0);
      if (view.fight?.gust?.clearedDropIds !== undefined) this.gustIndex = view.fight.gust.index;
      this.presentation.setGust(undefined, view.serverNow);
      this.phase = 0;
      if (view.fight) this.resetBossPose();
    }
    this.collector.reconcile(view.fight?.drops ?? []);
    this.view = view;
    // A delayed response must not rewind an already visible inhale/blow or expiry.
    this.lastResponseAt = performance.now() - Math.max(0, predictedNow - view.serverNow);
    // Returning during cooldown keeps the defeated face without replaying a reward.
    if (view.status === 'cooldown' && !this.won && !reward) this.resetBossPose(true);
    this.petTargets.forEach(target => target.setTargetable(view.status === 'playing'));
    this.presentation.setPlaying(view.status === 'playing');
    eventBus.emit('halloweenBoss:view', { view, reward,
      ...(view.status === 'inactive' ? { message: 'Event jest teraz zamknięty. Możesz wrócić na mapę.' } : {}) });
    const incoming = new Map((view.fight?.drops ?? []).map(candy => [candy.id, candy]));
    const collected = (view.fight?.collected ?? (reward ? view.goal : 0)) > (previous?.fight?.collected ?? 0);
    const picked = new Set(view.collectedDropIds ?? []);
    const gust = view.fight?.gust;
    const swept = new Set(gust?.clearedDropIds ?? []);
    // Only this request can reject its submitted contacts. An older response
    // must not undo a pickup that happened while it was in flight.
    if (view.fight?.id === previous?.fight?.id && view.collectedDropIds !== undefined) {
      const rejected = submittedIds.filter(id => incoming.has(id) && !picked.has(id));
      this.collector.release(rejected);
      for (const id of rejected) {
        const drop = this.drops.get(id);
        if (!drop?.collecting || drop.blownByGust) continue;
        drop.collecting = false;
        if (drop.pickupSprite) { this.tweens.killTweensOf(drop.pickupSprite); drop.pickupSprite.destroy(); drop.pickupSprite = undefined; }
        drop.sprite.setPosition(drop.candy.x, drop.candy.y).setVisible(true);
        drop.glow.setVisible(true); drop.rottenFx?.setPending(false);
      }
    }
    for (const [id, drop] of this.drops) {
      if (incoming.has(id)) continue;
      if (swept.has(id)) {
        if (gust && this.estimatedServerNow() - gust.blowAt < 1600) this.blowDrop(id, gust.index);
        this.drops.delete(id); drop.rottenFx?.destroy(); drop.glow.destroy();
        this.tweens.killTweensOf(drop.sprite); drop.sprite.destroy();
        continue;
      }
      const confirmed = picked.has(id) || (view.collectedDropIds === undefined && collected);
      if (!drop.rottenFx && confirmed && !drop.collecting) this.previewPickup(id);
      this.drops.delete(id);
      if (drop.rottenFx) {
        drop.rottenFx.retire();
        if (picked.has(id)) this.popup(this.player.x, this.player.y - 65, `−${halloween.boss.rotten.penalty}`, '#4e742e');
        continue;
      }
      drop.glow.destroy();
      this.tweens.killTweensOf(drop.sprite);
      if (confirmed) {
        this.popup(this.player.x, this.player.y - 65, `+${drop.candy.value}`);
        drop.sprite.destroy();
      } else drop.sprite.destroy();
    }
    let newDrops = 0;
    for (const candy of incoming.values()) {
      if (this.drops.has(candy.id)) continue;
      newDrops++;
      const spoiled = candy.kind === 'rotten';
      const size = spoiled ? 72 : candy.value >= 10 ? 76 : candy.value >= 5 ? 64 : 52;
      const glow = this.add.circle(candy.x, candy.y, size * 0.6, spoiled ? 0x91be52 : candy.value >= 10 ? 0xffdc74 : 0xfff3c5, 0.38).setDepth(1);
      const sprite = this.add.image(this.boss.x, this.boss.y + 50, spoiled ? 'boss_halloween_rotten_candy' : 'ui_candy').setDisplaySize(size, size).setDepth(5);
      if (!spoiled && candy.value >= 10) sprite.setTint(0xffd465);
      const delay = Math.min(900, Math.max(0, candy.availableAt - view.serverNow));
      const startY = sprite.y;
      this.tweens.add({ targets: sprite, x: candy.x, y: candy.y, angle: candy.value >= 5 ? 30 : -20,
        duration: delay || 150, ease: 'Linear', onUpdate: (tween: Phaser.Tweens.Tween) => {
          sprite.y = Phaser.Math.Linear(startY, candy.y, tween.progress) - Math.sin(tween.progress * Math.PI) * 75;
        } });
      this.drops.set(candy.id, { candy, sprite, glow,
        ...(spoiled ? { rottenFx: new HalloweenRottenCandyFx(this, candy, sprite, glow) } : {}) });
    }
    if (newDrops) this.presentation.candyBurst();
    const phase = Math.floor((view.fight?.collected ?? 0) / view.goal * 3);
    if (phase > this.phase && !reward) { this.phase = phase; this.popup(450, 195, phase === 1 ? 'Ale słodko!' : 'Jeszcze trochę!'); this.hop(); }
    if (reward) this.celebrate();
  }
  private blowDrop(id: string, index: number): void {
    const drop = this.drops.get(id);
    if (!drop || drop.blownByGust) return;
    drop.blownByGust = index;
    if (drop.collecting || !drop.sprite.active) return;
    this.tweens.killTweensOf(drop.sprite);
    drop.sprite.setVisible(false); drop.glow.setVisible(false); drop.rottenFx?.setPending(true);
    const sprite = this.add.image(drop.candy.x, drop.candy.y, drop.sprite.texture.key)
      .setDisplaySize(drop.sprite.displayWidth, drop.sprite.displayHeight).setTint(drop.sprite.tintTopLeft)
      .setDepth(11).setName('halloween-gust-candy');
    const angle = Math.atan2(drop.candy.y - this.bossBaseY, drop.candy.x - 450);
    this.tweens.add({ targets: sprite, x: sprite.x + Math.cos(angle) * 1150,
      y: sprite.y + Math.sin(angle) * 1150, angle: 300 * Math.sign(Math.cos(angle) || 1),
      alpha: 0, scaleX: sprite.scaleX * 0.6, scaleY: sprite.scaleY * 0.6,
      duration: 1350, ease: 'Cubic.easeIn', onComplete: () => sprite.destroy() });
  }
  private startGust(gust: BossGust, now: number): void {
    if (gust.index <= this.gustIndex || now < gust.blowAt) return;
    this.gustIndex = gust.index;
    if (now >= gust.endsAt) return; // Never replay an old push on reentry.
    for (const id of this.drops.keys()) this.blowDrop(id, gust.index);
    this.collector.clear(); this.tapDestination = null;
    const push = (x: number, y: number, distance: number, margin: number) => {
      const dx = x - 450, dy = y - this.bossBaseY, length = Math.hypot(dx, dy) || 1;
      return { from: {x,y}, to: { x: Phaser.Math.Clamp(x + dx / length * distance, margin, 900 - margin),
        y: Phaser.Math.Clamp(y + dy / length * distance, 380 + margin, 1280 - margin) } };
    };
    const playerPush = push(this.player.x, this.player.y, halloween.boss.gust.playerPush, 35);
    playerPush.to.y = 1260;
    const petYs=this.petAttackSlots.map(slot=>slot.y);
    const petOffset=Math.min(Math.max(halloween.boss.gust.petPush,900-Math.min(900,...petYs)),1050-Math.max(0,...petYs));
    this.gustMotion = { startedAt: performance.now(), player: playerPush,
      pets: this.pets.map((pet, i) => {
        const path = push(pet.sprite.x, pet.sprite.y, halloween.boss.gust.petPush, this.petFootprints[i]! / 2);
        // Preserve the reserved spacing while the wind holds the whole formation back.
        path.to={x:this.petAttackSlots[i]!.x,y:this.petAttackSlots[i]!.y+petOffset};
        pet.knockback(450, this.bossBaseY, 0); // Release the attack; the scene animates the displacement.
        return path;
      }) };
    this.gustCamera=true; this.layoutGustCamera(true);
    this.cameras.main.shake(280, 0.003);
  }
  private previewPickup(id: string): void {
    const drop = this.drops.get(id);
    if (!drop || drop.collecting) return;
    drop.collecting = true;
    this.tweens.killTweensOf(drop.sprite);
    drop.sprite.setVisible(false); drop.glow.setVisible(false);
    drop.rottenFx?.setPending(true);
    const candy = drop.candy;
    const sprite = this.add.image(candy.x, candy.y, drop.sprite.texture.key)
      .setDisplaySize(drop.sprite.displayWidth, drop.sprite.displayHeight).setTint(drop.sprite.tintTopLeft).setDepth(10);
    drop.pickupSprite = sprite;
    if (drop.rottenFx) {
      for (let i = 0; i < 6; i++) {
        const angle = i * Math.PI / 3;
        const puff = this.add.circle(candy.x, candy.y, 7, 0x91be52, 0.7).setDepth(11);
        this.tweens.add({ targets: puff, x: candy.x + Math.cos(angle) * 36, y: candy.y + Math.sin(angle) * 24 - 15,
          scale: 0, alpha: 0, duration: 350, onComplete: () => puff.destroy() });
      }
      this.tweens.add({ targets: sprite, scaleX: sprite.scaleX * 1.2, scaleY: 0, alpha: 0, angle: 45,
        duration: 240, onComplete: () => { sprite.destroy(); drop.pickupSprite = undefined; } });
    } else {
      this.presentation.pickup(candy.x, candy.y, candy.value);
      this.tweens.add({ targets: sprite, x: this.player.x, y: this.player.y - 25, scale: 0, alpha: 0,
        duration: 240, onComplete: () => { sprite.destroy(); drop.pickupSprite = undefined; } });
    }
    // The meter, signed popup and reward still come exclusively from the server.
  }
  private estimatedServerNow(): number {
    return (this.view?.serverNow ?? 0) + Math.max(0, performance.now() - this.lastResponseAt);
  }
  private resetBossPose(preserveVictory = false): void {
    this.won = preserveVictory;
    const landscape = this.scale.width > this.scale.height && this.scale.height < 600;
    this.bossBaseY = landscape ? 440 : 300;
    this.presentation.reset(this.bossBaseY, landscape ? 330 : 380, this.won);
  }
  private showConnectionMessage(error?: unknown): void {
    if (error instanceof ApiError && error.code === ECONOMY_RESYNC_CODE) {
      eventBus.emit('halloweenBoss:view', { view: this.view, message: ECONOMY_RESYNC_MESSAGE });
      return;
    }
    eventBus.emit('halloweenBoss:view', { view: this.view, message: 'Czekamy na połączenie. Twój zapisany postęp jest bezpieczny.' });
  }
  private hop(): void {
    this.presentation.hit();
  }
  private popup(x: number, y: number, text: string, color = '#704878'): void {
    const label = this.add.text(x, y, text, { fontFamily: 'Nunito, sans-serif', fontSize: '28px', fontStyle: 'bold', color, stroke: '#fff5df', strokeThickness: 6 }).setOrigin(0.5).setDepth(30);
    this.tweens.add({ targets: label, y: y - 45, alpha: 0, duration: 900, onComplete: () => label.destroy() });
  }
  private celebrate(): void {
    this.won = true;
    this.presentation.celebrate();
  }
  update(time: number, delta: number): void {
    if (this.dead || !this.player) return;
    if (this.introDecisionPending || this.introQueued || this.introPlaying) {
      this.player.sprite.body.setVelocity(0);
      if (this.introDecisionPending && !this.busy && time >= this.nextPulse) void this.prepareEncounter();
      if (this.introQueued && !useGameStore.getState().isTeleporting && !this.scene.isActive('TeleportScene')) this.startIntro();
      return;
    }
    const serverNow = this.estimatedServerNow();
    const gust = this.view?.fight?.gust;
    if (gust) this.startGust(gust, serverNow);
    const gustActive = !!gust && serverNow >= gust.startedAt && serverNow < gust.endsAt;
    if (this.gustCamera && !gustActive && !this.gustMotion) { this.gustCamera=false; this.layout(); }
    else if (!this.gustCamera && this.retreatFollow !== (this.player.y>930)) this.layout();
    this.petTargets.forEach(target => target.setTargetable(this.view?.status === 'playing' && (!gustActive || serverNow < gust!.blowAt)));
    this.presentation.setGust(gust, serverNow); this.presentation.update();
    this.drops.forEach(drop => drop.rottenFx?.update(serverNow));
    const store = useGameStore.getState();
    const blocked = store.isTeleporting || !!store.openModal;
    if (blocked) { this.player.sprite.body.setVelocity(0); return; }
    const motion = this.gustMotion;
    const progress = motion ? Phaser.Math.Clamp((performance.now() - motion.startedAt) / halloween.boss.gust.knockbackMs, 0, 1) : 0;
    const eased = 1 - (1 - progress) ** 3;
    const position = (path: {from:{x:number;y:number};to:{x:number;y:number}}) => ({
      x: Phaser.Math.Linear(path.from.x, path.to.x, eased),
      y: Phaser.Math.Linear(path.from.y, path.to.y, eased) - Math.sin(progress * Math.PI) * 45,
    });
    if (motion) {
      const p = position(motion.player);
      this.player.sprite.body.reset(p.x, p.y); this.player.sprite.body.setVelocity(0);
      this.player.sprite.setAngle(Math.sin(progress * Math.PI * 2) * 8);
      if (performance.now()-motion.startedAt >= halloween.boss.gust.knockbackMs+halloween.boss.gust.holdMs) {
        this.gustMotion = undefined; this.player.sprite.setAngle(0);
      } else if (progress>=1) this.player.sprite.setAngle(Math.sin(performance.now()/70)*4);
    } else this.player.update(time, delta / 1000);
    const movement = this.controls.getMovement();
    if (movement.x || movement.y) this.tapDestination = null;
    if (this.tapDestination && !motion) {
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.tapDestination.x, this.tapDestination.y);
      if (distance < 12) { this.tapDestination = null; this.player.sprite.body.setVelocity(0); }
      else this.physics.moveTo(this.player.sprite, this.tapDestination.x, this.tapDestination.y, this.player.speed);
    }
    this.pets.forEach((pet, index) => {
      const target = this.petTargets[index]!;
      const slot = this.petAttackSlots[index]!;
      if (pet.isIdle() && target.isTargetable()) pet.assignTarget(target);
      pet.update(delta / 1000, time, (x, y) => {
        if (motion?.pets[index]) return position(motion.pets[index]!);
        if (pet.state !== 'ATTACKING' || !target.isTargetable()) return { x, y };
        // Ease into the reserved slot after entering attack range, instead of
        // leaving everyone up to 40px short and allowing big sprites to overlap.
        const settle = 1 - Math.exp(-delta / 70);
        return { x: Phaser.Math.Linear(x, slot.x, settle), y: Phaser.Math.Linear(y, slot.y, settle) };
      });
    });
    if (this.view?.fight) {
      const waitingForSweep = gust && serverNow >= gust.blowAt && gust.clearedDropIds === undefined;
      if (!waitingForSweep && (!gustActive || serverNow < gust!.blowAt)) {
        for (const candy of this.collector.observe(this.view.fight.drops.filter(drop => !this.drops.get(drop.id)?.blownByGust),
          { x: this.player.x, y: this.player.y }, serverNow)) {
          this.previewPickup(candy.id);
        }
      }
      const pickupDue = this.collector.pending && time - this.lastPulseStartedAt >= halloween.boss.pickupPulseMinMs;
      const gustDue = waitingForSweep && time - this.lastPulseStartedAt >= halloween.boss.pickupPulseMinMs;
      if (!this.busy && (pickupDue || gustDue || time >= this.nextPulse)) void this.pulse();
    } else if (!this.busy && time >= this.nextPulse && (!this.view || this.view.status === 'ready' ||
      (this.view.status === 'cooldown' && serverNow >= this.view.nextAvailableAt))) {
      void this.enter();
    }
  }
}
